// ─── CMS Pages manager ───────────────────────────────────────────────────────
// In-app admin for editable content pages (Terms, Privacy, Help, custom …).
// Left: list of pages + New. Right: editor — title, slug, published, meta, and
// the shared WYSIWYG RichContentEditor for the body. Backed by app_cms
// (/api/cms/pages/). Public pages render at /page/<slug>.
// ──────────────────────────────────────────────────────────────────────────────
import { useState, useEffect, useCallback } from 'react'
import { Link } from 'react-router-dom'
import { Loader, Plus, Trash2, ExternalLink, Check, AlertCircle } from 'lucide-react'
import { AppHeader } from '../components/AppHeader'
import { RichContentEditor } from '../components/dashboard/RichContentEditor'
import {
  listCmsPages, createCmsPage, updateCmsPage, deleteCmsPage,
} from '../lib/cmsApi'

const BLANK = { title: '', slug: '', content: '', meta_description: '', is_published: true }

export default function CmsPagesPage() {
  const [pages, setPages]     = useState([])
  const [loading, setLoading] = useState(true)
  const [selected, setSelected] = useState(null)  // slug of the page being edited, or '__new__'
  const [draft, setDraft]     = useState(null)     // working copy of the selected page
  const [saving, setSaving]   = useState(false)
  const [error, setError]     = useState(null)
  const [banner, setBanner]   = useState(null)     // { type: 'ok'|'err', text }

  const refresh = useCallback(async () => {
    setLoading(true); setError(null)
    try {
      setPages(await listCmsPages())
    } catch (e) {
      setError(e?.response?.status === 401 || e?.response?.status === 403
        ? 'You need admin access to manage CMS pages.'
        : 'Could not load pages.')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { refresh() }, [refresh])

  const openNew = () => { setSelected('__new__'); setDraft({ ...BLANK }); setBanner(null) }
  const openPage = (p) => { setSelected(p.slug); setDraft({ ...p }); setBanner(null) }
  const setField = (k, v) => setDraft(d => ({ ...d, [k]: v }))

  const handleSave = async () => {
    if (!draft) return
    if (!draft.title.trim()) { setBanner({ type: 'err', text: 'Title is required.' }); return }
    setSaving(true); setBanner(null)
    try {
      const payload = {
        title: draft.title.trim(),
        slug: (draft.slug || '').trim(),
        content: draft.content || '',
        meta_description: draft.meta_description || '',
        is_published: !!draft.is_published,
      }
      const saved = selected === '__new__'
        ? await createCmsPage(payload)
        : await updateCmsPage(selected, payload)
      await refresh()
      setSelected(saved.slug); setDraft({ ...saved })
      setBanner({ type: 'ok', text: 'Saved.' })
    } catch (e) {
      const data = e?.response?.data
      const detail = data && typeof data === 'object'
        ? Object.entries(data).map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(' ') : v}`).join(' · ')
        : ''
      setBanner({ type: 'err', text: detail || 'Save failed. Please try again.' })
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async () => {
    if (selected === '__new__' || !draft) { setSelected(null); setDraft(null); return }
    if (!window.confirm(`Delete "${draft.title}"? This cannot be undone.`)) return
    setSaving(true)
    try {
      await deleteCmsPage(selected)
      setSelected(null); setDraft(null)
      await refresh()
    } catch {
      setBanner({ type: 'err', text: 'Delete failed.' })
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <AppHeader />
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex items-center justify-between mb-5">
          <h1 className="text-xl font-bold text-[#4D4D4D]">CMS Pages</h1>
          <button onClick={openNew}
            className="flex items-center gap-1.5 text-sm text-white bg-[#358ECD] hover:bg-[#2d7ab8] px-3 py-1.5 rounded-lg transition font-medium">
            <Plus className="w-4 h-4" /> New Page
          </button>
        </div>

        {error && (
          <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-4 py-2 mb-4">
            <AlertCircle className="w-4 h-4" /> {error}
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-[280px_1fr] gap-6">
          {/* ── Page list ── */}
          <div className="bg-white border border-gray-200 rounded-xl overflow-hidden h-fit">
            <div className="px-4 py-2.5 text-xs font-semibold uppercase tracking-wide text-gray-400 border-b border-gray-100">
              Pages ({pages.length})
            </div>
            {loading ? (
              <div className="flex justify-center py-8"><Loader className="w-5 h-5 animate-spin text-[#358ECD]" /></div>
            ) : pages.length === 0 ? (
              <p className="text-sm text-gray-400 px-4 py-6 text-center">No pages yet. Create one.</p>
            ) : (
              <ul>
                {pages.map(p => (
                  <li key={p.slug}>
                    <button onClick={() => openPage(p)}
                      className={`w-full text-left px-4 py-2.5 border-b border-gray-50 last:border-b-0 hover:bg-gray-50 transition ${selected === p.slug ? 'bg-[#E8F1F8]' : ''}`}>
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-sm text-gray-800 truncate">{p.title}</span>
                        {!p.is_published && <span className="text-[10px] text-gray-400 border border-gray-200 rounded px-1">draft</span>}
                      </div>
                      <div className="text-[11px] text-gray-400 truncate">/{p.slug}</div>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* ── Editor ── */}
          <div className="bg-white border border-gray-200 rounded-xl p-5">
            {!draft ? (
              <p className="text-sm text-gray-400 text-center py-16">Select a page to edit, or create a new one.</p>
            ) : (
              <>
                {banner && (
                  <div className={`flex items-center gap-2 text-sm rounded-lg px-3 py-2 mb-4 ${banner.type === 'ok' ? 'text-green-700 bg-green-50 border border-green-200' : 'text-red-600 bg-red-50 border border-red-200'}`}>
                    {banner.type === 'ok' ? <Check className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />} {banner.text}
                  </div>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                  <label className="text-xs font-semibold text-gray-600">Title
                    <input type="text" value={draft.title} onChange={e => setField('title', e.target.value)}
                      className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#358ECD]" />
                  </label>
                  <label className="text-xs font-semibold text-gray-600">Slug
                    <span className="ml-1 font-normal text-gray-400">(blank = from title)</span>
                    <input type="text" value={draft.slug} onChange={e => setField('slug', e.target.value)}
                      placeholder="auto"
                      className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#358ECD]" />
                  </label>
                </div>

                <label className="block text-xs font-semibold text-gray-600 mb-4">Meta description
                  <input type="text" value={draft.meta_description} onChange={e => setField('meta_description', e.target.value)}
                    className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#358ECD]" />
                </label>

                <label className="flex items-center gap-2 text-sm text-gray-700 mb-4">
                  <input type="checkbox" checked={!!draft.is_published} onChange={e => setField('is_published', e.target.checked)}
                    className="w-4 h-4 accent-[#358ECD]" />
                  Published
                </label>

                <div className="text-xs font-semibold text-gray-600 mb-1">Content</div>
                <RichContentEditor
                  key={selected}
                  initialContent={draft.content || ''}
                  onChange={(html) => setField('content', html)}
                />

                <div className="flex items-center gap-2 mt-5">
                  <button onClick={handleSave} disabled={saving}
                    className="flex items-center gap-1.5 text-sm text-white bg-[#358ECD] hover:bg-[#2d7ab8] px-4 py-2 rounded-lg transition font-medium disabled:opacity-50">
                    {saving && <Loader className="w-4 h-4 animate-spin" />} Save
                  </button>
                  {selected !== '__new__' && (
                    <>
                      <button onClick={handleDelete} disabled={saving}
                        className="flex items-center gap-1.5 text-sm text-red-500 hover:text-red-700 border border-red-200 hover:bg-red-50 px-4 py-2 rounded-lg transition disabled:opacity-50">
                        <Trash2 className="w-4 h-4" /> Delete
                      </button>
                      <Link to={`/page/${selected}`} target="_blank"
                        className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-[#358ECD] px-3 py-2 transition ml-auto">
                        <ExternalLink className="w-4 h-4" /> View
                      </Link>
                    </>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
